import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Building, Users, Settings, Trash2, Edit, Plus, Eye } from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';
import GymLogoUploadField from './GymLogoUploadField';

export default function GymManager() {
    const { getAuthenticatedApi, isAdmin } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [gyms, setGyms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [createdOwner, setCreatedOwner] = useState(location.state?.createdOwner ?? null);
    const [showCreateSuccess, setShowCreateSuccess] = useState(Boolean(location.state?.gymCreated));
    const [editingGym, setEditingGym] = useState(null);
    useEffect(() => {
        if (location.state?.gymCreated) {
            setCreatedOwner(location.state.createdOwner ?? null);
            setShowCreateSuccess(true);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.pathname, location.state, navigate]);

    useEffect(() => {
        fetchGyms();
    }, []);

    const fetchGyms = async () => {
        try {
            setLoading(true);
            const api = getAuthenticatedApi();
            const response = await api.get('/gyms.php');
            setGyms(response.data);
            setError(null);
        } catch (err) {
            console.error('Errore nel caricamento palestre:', err);
            setError(t('admin.gymManager.loadingError'));
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateGym = async (gymId, updateData) => {
        try {
            const api = getAuthenticatedApi();
            await api.put(`/gyms.php?id=${gymId}`, updateData);
            await fetchGyms();
            setEditingGym(null);
            setError(null);
        } catch (err) {
            console.error('Errore nell\'aggiornamento palestra:', err);
            setError(err.response?.data?.error || 'Errore nell\'aggiornamento della palestra');
        }
    };

    const handleDeleteGym = async (gymId) => {
        // eslint-disable-next-line no-restricted-globals
        if (!confirm('Sei sicuro di voler eliminare questa palestra? Questa azione non può essere annullata.')) {
            return;
        }

        try {
            const api = getAuthenticatedApi();
            await api.delete(`/gyms.php?id=${gymId}`);
            await fetchGyms();
            setError(null);
        } catch (err) {
            console.error('Errore nell\'eliminazione palestra:', err);
            setError(err.response?.data?.error || 'Errore nell\'eliminazione della palestra');
        }
    };

    const handleViewGym = (gym) => {
        navigate(`/admin/gyms/${gym.id}`, { state: { gymName: gym.name } });
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                            <Building className="text-blue-600" />
                            {t('admin.gymManager.listTitle')}
                        </h1>
                        <p className="text-gray-600 dark:text-gray-300 mt-2">
                            {t('admin.gymManager.listDescription')}
                        </p>
                    </div>

                    {isAdmin() && (
                        <Link
                            to="/admin/gyms/new"
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shrink-0"
                        >
                            <Plus size={20} />
                            {t('admin.gymManager.newGym')}
                        </Link>
                    )}
                </div>

                {showCreateSuccess && (
                    <div className="bg-green-100 border border-green-400 text-green-800 dark:bg-green-900/30 dark:border-green-700 dark:text-green-200 px-4 py-3 rounded mb-6 space-y-2">
                        <p className="font-medium">{t('admin.gymManager.createSuccess')}</p>
                        {createdOwner?.username && (
                            <div className="text-sm rounded-md border border-green-300/60 dark:border-green-600/50 bg-white/90 dark:bg-gray-950/80 p-3 space-y-2">
                                <p className="font-mono">
                                    <span className="font-sans text-xs uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        {t('admin.gymManager.fieldUsername')}
                                    </span>
                                    <br />
                                    <span className="font-semibold text-gray-900 dark:text-gray-100">{createdOwner.username}</span>
                                </p>
                                {createdOwner.password && (
                                    <p className="font-mono">
                                        <span className="font-sans text-xs uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                            {t('admin.gymManager.fieldPassword')}
                                        </span>
                                        <br />
                                        <span className="font-semibold break-all text-gray-900 dark:text-gray-100">{createdOwner.password}</span>
                                    </p>
                                )}
                                <p className="font-sans text-xs text-green-900/80 dark:text-green-200/80 pt-1">
                                    {t('admin.gymManager.createSuccessCredentialsHint')}
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                        {error}
                    </div>
                )}

                {/* Edit Form */}
                {editingGym && (
                    <GymEditForm
                        gym={editingGym}
                        onSubmit={(updateData) => handleUpdateGym(editingGym.id, updateData)}
                        onCancel={() => setEditingGym(null)}
                    />
                )}

                {/* Gyms Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {gyms.map((gym) => (
                        <GymCard
                            key={gym.id}
                            gym={gym}
                            onView={handleViewGym}
                            onEdit={setEditingGym}
                            onDelete={handleDeleteGym}
                            isAdmin={isAdmin()}
                        />
                    ))}
                </div>

                {gyms.length === 0 && (
                    <div className="text-center py-12">
                        <Building size={48} className="mx-auto text-gray-400 mb-4" />
                        <p className="text-gray-500 text-lg">{t('admin.gymManager.noGymsRegistered')}</p>
                        <p className="text-gray-400 mb-4">{t('admin.gymManager.createFirstGym')}</p>
                        {isAdmin() && (
                            <Link
                                to="/admin/gyms/new"
                                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                            >
                                <Plus size={20} />
                                {t('admin.gymManager.newGym')}
                            </Link>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// Componente Card per singola palestra
function GymCard({ gym, onView, onEdit, onDelete, isAdmin }) {
    const { t } = useTranslation();
    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return 'bg-green-100 text-green-800';
            case 'inactive': return 'bg-gray-100 text-gray-800';
            case 'suspended': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'active': return t('admin.gymManager.status.active');
            case 'inactive': return t('admin.gymManager.status.inactive');
            case 'suspended': return t('admin.gymManager.status.suspended');
            default: return status;
        }
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
            <div className="flex justify-between items-start mb-4">
                <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {gym.name}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">
                        {gym.email}
                    </p>
                </div>
                <div className="flex flex-col items-end gap-1">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(gym.status)}`}>
                        {getStatusText(gym.status)}
                    </span>
                    {/* WEBAPP-031: Piano e stato attivazione */}
                    <span className={`px-2 py-0.5 rounded text-xs ${(gym.plan_active === 0 || gym.plan_active === false) ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'}`}>
                        {(gym.plan_active === 0 || gym.plan_active === false) ? 'Piano non attivo' : 'Piano attivo'}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                        {gym.plan === 'elite' ? 'Elite' : gym.plan === 'start' ? 'Start' : 'Premium'}
                    </span>
                    {gym.billing_status && (
                        <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                            {gym.billing_status === 'trialing' && 'Trial'}
                            {gym.billing_status === 'complimentary' && 'Omaggio'}
                            {gym.billing_status === 'expired' && 'Scaduto'}
                            {gym.billing_status === 'active' && 'Pagante'}
                        </span>
                    )}
                </div>
            </div>

            <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                    <Users size={16} className="mr-2" />
                    {t('admin.gymManager.membersAndTrainers', { 
                        members: gym.total_members || 0, 
                        trainers: gym.total_trainers || 0 
                    })}
                </div>
                {gym.phone && (
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        📞 {gym.phone}
                    </p>
                )}
                {gym.address && (
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        📍 {gym.address}
                    </p>
                )}
            </div>

            <div className="flex gap-2">
                <button
                    onClick={() => onView(gym)}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm flex items-center justify-center gap-1 transition-colors"
                >
                    <Eye size={16} />
                    {t('admin.gymManager.actions.view')}
                </button>

                {isAdmin && (
                    <>
                        <button
                            onClick={() => onEdit(gym)}
                            className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-2 rounded text-sm flex items-center justify-center transition-colors"
                        >
                            <Edit size={16} />
                        </button>
                        <button
                            onClick={() => onDelete(gym.id)}
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded text-sm flex items-center justify-center transition-colors"
                        >
                            <Trash2 size={16} />
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}


// Form per modificare palestra
function GymEditForm({ gym, onSubmit, onCancel }) {
    const [formData, setFormData] = useState({
        name: gym.name || '',
        email: gym.email || '',
        phone: gym.phone || '',
        address: gym.address || '',
        max_users: gym.max_users || 100,
        max_trainers: gym.max_trainers || 10,
        status: gym.status || 'active',
        plan: gym.plan || 'premium',
        plan_active: gym.plan_active !== 0 && gym.plan_active !== false,
        billing_status: gym.billing_status || 'active',
    });
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await onSubmit({
                ...formData,
                plan_active: formData.plan_active ? 1 : 0
            });
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                Modifica Palestra: {gym.name}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
                <GymLogoUploadField
                    gymId={gym.id}
                    logoFilename={gym.logo_filename}
                    compact
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Nome Palestra *
                        </label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Email *
                        </label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Telefono
                        </label>
                        <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Stato
                        </label>
                        <select
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        >
                            <option value="active">Attiva</option>
                            <option value="inactive">Inattiva</option>
                            <option value="suspended">Sospesa</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Max Utenti
                        </label>
                        <input
                            type="number"
                            name="max_users"
                            value={formData.max_users}
                            onChange={handleChange}
                            min="1"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Max Trainer
                        </label>
                        <input
                            type="number"
                            name="max_trainers"
                            value={formData.max_trainers}
                            onChange={handleChange}
                            min="1"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Accesso SaaS
                        </label>
                        <select
                            name="billing_status"
                            value={formData.billing_status}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        >
                            <option value="active">Pagante</option>
                            <option value="trialing">In trial</option>
                            <option value="complimentary">Omaggio / tester</option>
                            <option value="expired">Scaduto</option>
                        </select>
                    </div>

                    {/* WEBAPP-031: Livello funzionalità */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Livello funzionalità
                        </label>
                        <select
                            name="plan"
                            value={formData.plan}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        >
                            <option value="start">Start (no calendario/corsi)</option>
                            <option value="premium">Premium (tutto incluso)</option>
                            <option value="elite">Elite (tablet solo trainer)</option>
                        </select>
                    </div>
                    <div className="flex items-center pt-6">
                        <input
                            type="checkbox"
                            id="plan_active"
                            name="plan_active"
                            checked={formData.plan_active}
                            onChange={handleChange}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
                        />
                        <label htmlFor="plan_active" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Piano attivo (la palestra può accedere alla piattaforma)
                        </label>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Indirizzo
                    </label>
                    <textarea
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                </div>

                <div className="flex gap-4 pt-4">
                    <button
                        type="submit"
                        disabled={loading}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md flex items-center gap-2 disabled:opacity-50 transition-colors"
                    >
                        {loading ? <LoadingSpinner size="small" /> : <Settings size={20} />}
                        Salva Modifiche
                    </button>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-md transition-colors"
                    >
                        Annulla
                    </button>
                </div>
            </form>
        </div>
    );
}
